import React, { useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  TextField,
  Typography,
} from '@mui/material';

import { Metric } from '../../@types/Models';
import { Objective } from '../../@types/State';
import { metrics } from '../../Constants';
import { useIntelState } from '../../context/IntelState';
import { effects } from '../../Effects';
import { findKanbanItemFromId } from '../../helpers/GeneralHelpers';
import { letterize } from '../../helpers/NumberFormatting';

const opTypes = Object.entries(effects)
  .filter(
    ([key, value]) =>
      value.isBad === value.isRequestable && !value.isDuration && value.isBad
  )
  .map(([key, value]) => key);

export const followersMarks = [
  {
    value: 0,
    scaledValue: 0,
    label: '0',
  },
  {
    value: 25,
    scaledValue: 250,
    label: '250',
  },
  {
    value: 50,
    scaledValue: 500,
    label: '500',
  },
  {
    value: 75,
    scaledValue: 750,
    label: '750',
  },
  {
    value: 100,
    scaledValue: 1000,
    label: '1k',
  },
  {
    value: 125,
    scaledValue: 2000,
    label: '2k',
  },
  {
    value: 150,
    scaledValue: 2500,
    label: '2.5k',
  },
  {
    value: 175,
    scaledValue: 5000,
    label: '5k',
  },
  {
    value: 200,
    scaledValue: 10000,
    label: '10k',
  },
  {
    value: 225,
    scaledValue: 20000,
    label: '20k',
  },
];

const scale = (value: number) => {
  const previousMarkIndex = Math.floor(value / 25);
  const previousMark = followersMarks[previousMarkIndex];
  const remainder = value % 25;
  if (remainder === 0) {
    return previousMark.scaledValue;
  }
  const nextMark = followersMarks[previousMarkIndex + 1];
  const increment = (nextMark.scaledValue - previousMark.scaledValue) / 25;
  return remainder * increment + previousMark.scaledValue;
};

const ObjectiveSelector: React.FC = () => {
  const { intelState, intelDispatch } = useIntelState();
  const [metric, setMetric] = useState<Metric>(metrics[0]);
  const [metricValue, setMetricValue] = useState(1);
  const [opType, setOpType] = useState(opTypes[0]);
  const [opCount, setOpCount] = useState(0);

  const handleChange = (event: SelectChangeEvent) => {
    const metric = metrics.find((x) => x.name === event.target.value);
    if (metric) {
      setMetric(metric);
    }
  };

  const handleOpTypeChange = (event: SelectChangeEvent) => {
    setOpType(event.target.value);
  };

  const handleValueChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setMetricValue(newValue);
    }
  };

  const handleAddClick = () => {
    const selectedKanbanItem = findKanbanItemFromId(
      intelState.enemyPlan,
      intelState.selectedKanbanProvinceId
    );
    if (isOpType) {
      intelDispatch({
        type: 'ADD_PROVINCE_OBJECTIVE',
        payload: {
          objective: {
            type: opType,
            context: 4,
            value: opCount,
          } as Objective,
          province: selectedKanbanItem?.province,
        },
      });
      intelDispatch({
        type: 'ADD_PROVINCE_OP',
        payload: {
          op: opType,
          province: selectedKanbanItem?.province,
        },
      });
    } else {
      intelDispatch({
        type: 'ADD_PROVINCE_OBJECTIVE',
        payload: {
          objective: {
            type: metric.name,
            context: metric.context,
            value: scale(metricValue),
          } as Objective,
          province: selectedKanbanItem?.province,
        },
      });
    }
  };

  const isOpType = metric.name === 'OP count';
  return (
    <Box>
      <Typography variant='body1' textAlign='center'>
        Add an Objective
        <Button
          variant='outlined'
          onClick={handleAddClick}
          sx={{ ml: 2 }}
          size='small'
        >
          Add
        </Button>
      </Typography>
      <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
        <InputLabel id='metric-select-label'>Metric</InputLabel>
        <Select
          variant="standard"
          labelId='metric-select-label'
          id='metric-select'
          value={metric.name}
          label='Metric'
          size='small'
          onChange={handleChange}>
          {metrics.map((x: Metric) => (
            <MenuItem key={x.name} value={x.name}>
              {x.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!isOpType && (
        <Box
          sx={{
            my: 2,
            mx: 1
          }}>
          <Slider
            style={{ maxWidth: 500 }}
            value={metricValue}
            min={0}
            step={1}
            max={225}
            valueLabelFormat={letterize}
            marks={followersMarks}
            scale={scale}
            onChange={handleValueChange}
            valueLabelDisplay='auto'
            aria-labelledby='non-linear-slider'
          />
          <Typography variant='body2' gutterBottom sx={{ textAlign: 'left' }}>
            Target {metric.name}Amount: {letterize(scale(metricValue))}{' '}
          </Typography>
        </Box>
      )}
      {isOpType && (
        <Box>
          <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
            <InputLabel id='metric-select-label'>Op Type</InputLabel>
            <Select
              variant="standard"
              labelId='metric-select-label'
              id='metric-select'
              value={opType}
              label='Op Type'
              size='small'
              onChange={handleOpTypeChange}>
              {opTypes.map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="standard"
            sx={{ mt: 2 }}
            fullWidth
            name='op-count'
            size='small'
            type='number'
            label='count of op'
            InputProps={{ inputProps: { min: 1, max: 25 } }}
            value={opCount}
            onChange={(e) => setOpCount(Number(e.target.value))} />
        </Box>
      )}
    </Box>
  );
};

export default ObjectiveSelector;
